import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { BookDetailsModal } from './components/BookDetailsModal';
import MediaControlsBar from './components/MediaControlsBar';
import { NowPlayingSidebarSection } from './components/NowPlayingSidebarSection';
import Home from './pages/Home';
import About from './pages/About';
import Accessibility from './pages/Accessibility';
import Settings from './pages/Settings';
import ReadingsDesigner from './pages/ReadingsDesigner';
import MyLibrary from './pages/MyLibrary';
import NowPlayingPage from './pages/NowPlaying';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Landing from './pages/Landing';
import Compliance from './pages/Compliance';
import AddToLibrary from './pages/AddToLibrary';
import DistrictSignup from './pages/DistrictSignup';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/toaster';
import { Button } from './components/ui/button';
import { useAuth } from './contexts/AuthContext';
import { useNowPlaying } from './contexts/NowPlayingContext';
import { BookOpen, Info, Shield, Library, Settings as SettingsIcon, User, Menu } from 'lucide-react';
import { ROUTES } from './config/routes';
import { useDocumentTitle } from './hooks/useDocumentTitle';

/* Route → page title map (WCAG 2.4.2). NowPlaying could later override this
 * with the book title from inside the page itself. */
const ROUTE_TITLES = {
  [ROUTES.LANDING]: 'Classic Literature, Accessible to Every Student',
  [ROUTES.COMPLIANCE]: 'ADA Title II Compliance',
  [ROUTES.LOGIN]: 'Sign In',
  [ROUTES.DISTRICT_SIGNUP]: 'Sign Up Your District',
  [ROUTES.AUTH_CALLBACK]: 'Signing In…',
  [ROUTES.HOME]: 'Discover',
  [ROUTES.MY_LIBRARY]: 'My Library',
  [ROUTES.NOW_PLAYING]: 'Now Playing',
  [ROUTES.READINGS_DESIGNER]: 'Readings Designer',
  [ROUTES.SETTINGS]: 'Settings',
  [ROUTES.ABOUT]: 'About',
  [ROUTES.ACCESSIBILITY]: 'Accessibility',
};

function deriveTitle(pathname) {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  if (pathname.startsWith('/add/')) return 'Add to Library';
  return null;
}

function App() {
  const [selectedBook, setSelectedBook] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isTeacher } = useAuth();
  const {
    bookId: currentBookId,
    setBookId,
    clearBook,
    audioRef,
    handleAudioPlay,
    handleAudioPause,
    handleAudioEnded,
    manifest,
    loading: bookLoading,
    isBuffering,
    isPlaying,
    currentChapter,
  } = useNowPlaying();

  useDocumentTitle(deriveTitle(location.pathname));

  /* Player status announcement (WCAG 4.1.3). Derives a single line of text
   * for screen readers from the current player state. Intentionally minimal
   * to avoid noisy announcements — only transitions that meaningfully
   * change what the user can/should do. */
  const playerStatus = (() => {
    if (!currentBookId) return '';
    if (bookLoading) return 'Loading book';
    if (manifest?.status === 'generating') {
      return `Preparing audio: ${manifest.chunksReady ?? 0} of ${manifest.totalChunkCount ?? 0} sections ready`;
    }
    if (isBuffering) return 'Buffering';
    if (isPlaying) return currentChapter ? `Playing: ${currentChapter.title}` : 'Playing';
    return 'Paused';
  })();

  // When user clicks "Read Now" on a book card, set the book + navigate to /now-playing
  const handleSelectBook = (bookId) => {
    setBookId(bookId);
    navigate(ROUTES.NOW_PLAYING);
  };

  const handleShowDetails = (book) => {
    setSelectedBook(book);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedBook(null);
  };

  // Clear player when user logs out or navigates to a public marketing page
  useEffect(() => {
    if (!isAuthenticated || location.pathname === ROUTES.LANDING || location.pathname === ROUTES.LOGIN || location.pathname === ROUTES.COMPLIANCE || location.pathname === ROUTES.DISTRICT_SIGNUP) {
      clearBook();
    }
  }, [isAuthenticated, location.pathname, clearBook]);

  // Reset scroll position on every route change. The <main> element below
  // has its own scroll container (overflow-y-auto), so scrolling the window
  // alone isn't enough — we scroll both.
  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Hide sidebar on public marketing / standalone pages
  const isLandingPage =
    location.pathname === ROUTES.LANDING ||
    location.pathname === ROUTES.COMPLIANCE ||
    location.pathname === ROUTES.DISTRICT_SIGNUP;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Skip-to-content link — first focusable element on every page (WCAG 2.4.1).
       * Hidden until focused; jumps keyboard users past the sidebar nav. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {/* Left Sidebar Navigation - hidden on landing page */}
      {!isLandingPage && (
        <aside className={`w-64 border-r bg-background/95 flex flex-col fixed h-screen z-50 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-64'
        }`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b">
          <Link to={ROUTES.HOME} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <BookOpen className="h-6 w-6" aria-hidden="true" />
            <div className="flex flex-col">
              <span className="font-bold text-lg leading-none font-serif">readwell</span>
              <span className="text-xs text-muted-foreground">K-12 Platform</span>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          <Link
            to={ROUTES.HOME}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              location.pathname === ROUTES.HOME
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <Library className="h-5 w-5" aria-hidden="true" />
            Discover
          </Link>

          <Link
            to={ROUTES.MY_LIBRARY}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              location.pathname === ROUTES.MY_LIBRARY
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            <BookOpen className="h-5 w-5" aria-hidden="true" />
            My Library
          </Link>

          {/* Now Playing - always visible, empty when nothing's playing */}
          <NowPlayingSidebarSection />

          {/* Only show Readings Designer for teachers */}
          {isTeacher && (
            <Link
              to={ROUTES.READINGS_DESIGNER}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === ROUTES.READINGS_DESIGNER
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              <SettingsIcon className="h-5 w-5" aria-hidden="true" />
              Readings Designer
            </Link>
          )}
        </nav>

        {/* Footer in Sidebar */}
        <div className={`border-t p-4 transition-all duration-300 ${currentBookId ? 'pb-32' : ''}`}>
          {!isAuthenticated && (
            <div className="mb-4">
              <Link to={ROUTES.LOGIN}>
                <Button variant="default" className="w-full">
                  <User className="h-4 w-4 mr-2" aria-hidden="true" />
                  Sign In
                </Button>
              </Link>
            </div>
          )}
          <div className="space-y-2">
            {isAuthenticated && (
              <Link
                to={ROUTES.SETTINGS}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  location.pathname === ROUTES.SETTINGS
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <SettingsIcon className="h-4 w-4" aria-hidden="true" />
                Settings
              </Link>
            )}
            <Link
              to={ROUTES.ABOUT}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname === ROUTES.ABOUT
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Info className="h-4 w-4" aria-hidden="true" />
              About
            </Link>
            <Link
              to={ROUTES.ACCESSIBILITY}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                location.pathname === ROUTES.ACCESSIBILITY
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <Shield className="h-4 w-4" aria-hidden="true" />
              Accessibility
            </Link>
          </div>
        </div>
      </aside>
      )}

      {/* Main Content Area - responsive layout */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${
        !isLandingPage ? (sidebarOpen ? 'ml-64' : 'ml-0') : ''
      }`}>
        {/* Floating hamburger button when sidebar is collapsed */}
        {!isLandingPage && !sidebarOpen && (
          <div className="fixed top-4 left-4 z-50">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              className="bg-background border shadow-md"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
        )}

        {/* Main Content */}
        <main
          id="main-content"
          tabIndex={-1}
          className={`flex-1 relative overflow-y-auto w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${currentBookId ? 'pb-32' : ''}`}
        >
          <Routes>
            {/* Public routes */}
            <Route path={ROUTES.LANDING} element={<Landing />} />
            <Route path={ROUTES.COMPLIANCE} element={<Compliance />} />
            <Route path={ROUTES.ADD_TO_LIBRARY} element={<AddToLibrary />} />
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.DISTRICT_SIGNUP} element={<DistrictSignup />} />

            {/* Auth callback - needed for OAuth flow */}
            <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallback />} />

            {/* Protected routes */}
            <Route
              path={ROUTES.SETTINGS}
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ABOUT}
              element={
                <ProtectedRoute>
                  <About />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.ACCESSIBILITY}
              element={
                <ProtectedRoute>
                  <Accessibility />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.HOME}
              element={
                <ProtectedRoute>
                  <Home onSelectBook={handleSelectBook} onShowDetails={handleShowDetails} />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MY_LIBRARY}
              element={
                <ProtectedRoute>
                  <MyLibrary onSelectBook={handleSelectBook} />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.NOW_PLAYING}
              element={
                <ProtectedRoute>
                  <NowPlayingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.READINGS_DESIGNER}
              element={
                <ProtectedRoute teacherOnly={true}>
                  <ReadingsDesigner />
                </ProtectedRoute>
              }
            />
          </Routes>

          {/* Book Details Modal */}
          {selectedBook && (
            <BookDetailsModal
              book={selectedBook}
              isOpen={isDetailsModalOpen}
              onClose={handleCloseDetails}
              onSelectBook={handleSelectBook}
              isSaved={false}
            />
          )}
        </main>
      </div>

      {/* Persistent audio element - rendered ONCE so playback survives route changes */}
      <audio
        ref={audioRef}
        preload="metadata"
        onPlay={handleAudioPlay}
        onPause={handleAudioPause}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />

      {/* Visually-hidden live region for player status (WCAG 4.1.3).
       * Only meaningful state transitions are announced — see playerStatus
       * derivation above. */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {playerStatus}
      </div>

      {/* Global media controls bar - shown whenever a book is loaded */}
      {isAuthenticated && currentBookId && <MediaControlsBar />}

      <Toaster />
    </div>
  );
}

export default App;
